// PostChain contract (§24.2, §12.8). WP7 owns the implementation. Tiers:
//   High    RenderPass → UnrealBloomPass (half res, 0.55 / 0.4 / 0.85) → FinalPass (9 taps) → OutputPass → SMAA
//   Medium  RenderPass → UnrealBloomPass (quarter res, 0.5) → FinalPass (9 taps) → OutputPass → FXAA
//   Low     RenderPass → FinalPass (5 taps, no grain) → OutputPass
//   Potato  no composer: `renderer.render` with tone mapping on the renderer. Moments that must show on
//           every tier (GAME! desaturation, the VHS replay look and rewind, MATCH POINT bars, the
//           Sudden Death pulse, flashes) copy the finished frame into a texture and draw the FinalPass
//           over it, only while they last (one copy and one quad: under SwiftShader a half-float
//           scene target cost several times the whole frame).
// The comic text-pop overlay is drawn after the chain, so pops stay crisp (no blur, bloom or grade).
import {
  Color, FramebufferTexture, HalfFloatType, LinearFilter, NeutralToneMapping, Scene, Vector2, Vector3, WebGLRenderTarget, type Camera, type PerspectiveCamera,
  type WebGLRenderer,
} from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { FXAAPass } from 'three/examples/jsm/postprocessing/FXAAPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import type { FrameView } from '../../game/FrameView';
import type { QualityTier } from '../Renderer';
import { createFinalMaterial, createFinalUniforms, GRADES, type FinalUniforms, type Grade } from './finalPass';

export interface PostChain {
  render(scene: Scene, camera: PerspectiveCamera, view: FrameView): void;
  setTier(t: QualityTier): void;
  pulseChromatic(amount: number, ms: number): void;
  setVhs(on: boolean): void;
  resize(w: number, h: number, dpr: number): void;
}

/** Per-frame inputs the Renderer supplies besides the view (all optional). */
export interface PostFrame {
  /** Look target (three.js world): the tilt-shift focus band follows its screen Y. */
  focus: Vector3 | null;
  /** GAME!: the winner's feet and head (three.js world), or null to keep color around the screen center. */
  winnerFeet: Vector3 | null;
  winnerHead: Vector3 | null;
  /** MATCH POINT letterbox bars wanted. */
  letterbox: boolean;
  /** 0..1 VHS rewind tear (replay transition). */
  wipe: number;
  /** Tilt-shift setting. */
  tiltShift: boolean;
}

/** WP7's chain: the contract plus the §11 screen effects VFX drive and the pop overlay. */
export interface GamePost extends PostChain {
  readonly tier: QualityTier;
  /** Screen-space refraction ring at a sim point (High only). */
  shockwave(x: number, y: number, z: number): void;
  redPulse(): void;
  flash(color: Color, amount: number, ms: number): void;
  setFrame(f: Partial<PostFrame>): void;
  /** A scene drawn on top of the finished frame (text pops), with its own camera. */
  setOverlay(scene: Scene, camera: Camera): void;
  /** True when the last frame went through the FinalPass (composer tiers, or a Potato moment). */
  readonly finalActive: boolean;
  /** Live tuning for verification (bloom threshold / strength / radius). */
  tuneBloom(threshold: number, strength: number, radius: number): void;
  /** Live tuning for the art review: overrides fields of a grade preset. */
  tuneGrade(preset: keyof typeof GRADES, g: Partial<Grade>): void;
  /** Verification: Potato moments through the old half-float scene target instead of a frame copy. */
  potatoViaTarget(on: boolean): void;
  readonly uniforms: FinalUniforms;
}

export const POST = {
  // §12.8 gives threshold 0.85 for a [0, 1] scene; under the §12.3 lights lit porcelain and frosting
  // reach ~1.0 in linear HDR, so 0.85 bloomed every bright surface into haze. 1.1 keeps bloom on
  // emissive glows and HDR sparks only (decision logged in docs/decisions/WP7.md).
  bloomHigh: { strength: 0.55, radius: 0.4, threshold: 1.1 },
  bloomMedium: { strength: 0.5, radius: 0.4, threshold: 1.1 },
  /** Tilt-shift: maximum blur 2.5 px at 1080p (§12.8), band width 0.35. */
  blurPx1080: 2.5, focusWidth: 0.35,
  vignette: 0.25, grain: 0.03,
  letterbox: 0.06, letterboxRate: 0.25,
  /** Seconds a grade change takes to blend in. */
  gradeBlendSec: 0.35, gameBlendSec: 0.3,
  waveSec: 0.42, waveRadius: 0.55,
  redSec: 0.9, redPeak: 0.55,
  wipeChroma: 0.004,
} as const;

/** Bloom at a fraction of the composer size (UnrealBloomPass halves internally). */
class ScaledBloom extends UnrealBloomPass {
  constructor(private readonly k: number, strength: number, radius: number, threshold: number) {
    super(new Vector2(256, 256), strength, radius, threshold);
  }
  override setSize(w: number, h: number): void {
    super.setSize(Math.max(2, Math.round(w * this.k)), Math.max(2, Math.round(h * this.k)));
  }
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function createPostChain(renderer: WebGLRenderer): GamePost {
  renderer.toneMapping = NeutralToneMapping;
  renderer.toneMappingExposure = 1;
  const u = createFinalUniforms();
  let tier: QualityTier = 'potato';
  let composer: EffectComposer | null = null;
  let bloom: ScaledBloom | null = null;
  let bloomTune: { threshold: number; strength: number; radius: number } | null = null;
  let renderPass: RenderPass | null = null;
  let finalPass: ShaderPass | null = null;
  // Potato moments: the finished frame copied into `potatoTex`, then the FinalPass over it. The older
  // path (the scene into a half-float target) stays behind `potatoViaTarget` for A/B measurements.
  let potatoRt: WebGLRenderTarget | null = null;
  let potatoTex: FramebufferTexture | null = null;
  let potatoViaTarget = false;
  const potatoMat = createFinalMaterial(u, 0, true);
  const potatoQuad = new FullScreenQuad(potatoMat);
  const potatoCopyMat = createFinalMaterial(u, 0, true, true);
  const potatoCopyQuad = new FullScreenQuad(potatoCopyMat);
  let overlayScene: Scene | null = null;
  let overlayCamera: Camera | null = null;
  let cssW = 1;
  let cssH = 1;
  let dpr = 1;
  let vhs = false;
  let finalActive = false;
  let lastNow = -1;
  let chromaAmount = 0;
  let chromaLeft = 0;
  let chromaTotal = 1;
  let flashAmount = 0;
  let flashLeft = 0;
  let flashTotal = 1;
  let waveLeft = 0;
  const waveWorld = new Vector3();
  let redLeft = 0;
  let gameMix = 0;
  const grade: Grade = { ...GRADES.normal };
  const presets: Record<keyof typeof GRADES, Grade> = {
    normal: { ...GRADES.normal }, gold: { ...GRADES.gold }, ghost: { ...GRADES.ghost }, game: { ...GRADES.game },
  };
  const frame: PostFrame = { focus: null, winnerFeet: null, winnerHead: null, letterbox: false, wipe: 0, tiltShift: true };
  const v3 = new Vector3();
  const v3b = new Vector3();
  const size = new Vector2();
  const tintTarget = new Color();

  function disposeComposer(): void {
    if (!composer) return;
    for (const p of composer.passes) p.dispose();
    composer.renderTarget1.dispose();
    composer.renderTarget2.dispose();
    composer = null;
    renderPass = null;
    finalPass = null;
  }

  function build(): void {
    disposeComposer();
    if (tier === 'potato') return;
    const c = new EffectComposer(renderer);
    c.setPixelRatio(dpr);
    c.setSize(cssW, cssH);
    renderPass = new RenderPass(new Scene(), undefined as unknown as Camera);
    c.addPass(renderPass);
    bloom = null;
    if (tier === 'high' || tier === 'medium') {
      const b = tier === 'high' ? POST.bloomHigh : POST.bloomMedium;
      bloom = new ScaledBloom(tier === 'high' ? 1 : 0.5, b.strength, b.radius, b.threshold);
      if (bloomTune) {
        bloom.threshold = bloomTune.threshold;
        bloom.strength = bloomTune.strength;
        bloom.radius = bloomTune.radius;
      }
      c.addPass(bloom);
    }
    finalPass = new ShaderPass(createFinalMaterial(u, tier === 'low' ? 5 : 9, false), 'tDiffuse');
    c.addPass(finalPass);
    c.addPass(new OutputPass());
    if (tier === 'high') c.addPass(new SMAAPass());
    else if (tier === 'medium') c.addPass(new FXAAPass());
    composer = c;
  }

  /** Steps the timed pulses and writes every uniform for this frame. */
  function updateUniforms(camera: PerspectiveCamera, view: FrameView, realDt: number): void {
    renderer.getDrawingBufferSize(size);
    u.uRes.value.copy(size);
    u.uTime.value = (u.uTime.value + realDt) % 1000;
    // Tilt-shift band on the look target's screen Y.
    let focusY = 0.5;
    if (frame.focus) {
      v3.copy(frame.focus).project(camera);
      focusY = Math.min(0.85, Math.max(0.15, (v3.y + 1) / 2));
    }
    u.uFocusY.value = lerp(u.uFocusY.value, focusY, Math.min(1, realDt * 6));
    u.uFocusW.value = POST.focusWidth;
    u.uBlurPx.value = frame.tiltShift ? POST.blurPx1080 * (size.y / 1080) : 0;

    // Grade: blend toward the view's preset.
    const target = presets[view.grade] ?? presets.normal;
    const k = Math.min(1, realDt / POST.gradeBlendSec);
    grade.lift = lerp(grade.lift, target.lift, k);
    grade.gamma = lerp(grade.gamma, target.gamma, k);
    grade.gain = lerp(grade.gain, target.gain, k);
    grade.sat = lerp(grade.sat, target.sat, k);
    grade.contrast = lerp(grade.contrast, target.contrast, k);
    grade.tintAmt = lerp(grade.tintAmt, target.tintAmt, k);
    tintTarget.set(target.tint);
    u.uTint.value.lerp(tintTarget, k);
    u.uLift.value.setScalar(grade.lift);
    u.uGamma.value.setScalar(grade.gamma);
    u.uGain.value.setScalar(grade.gain);
    u.uSat.value = grade.sat;
    u.uContrast.value = grade.contrast;
    u.uTintAmt.value = grade.tintAmt;

    // GAME!: saturation 0 except a soft circle around the winner (radius 2× its projected height).
    const wantGame = view.grade === 'game' ? 1 : 0;
    gameMix = wantGame > gameMix ? Math.min(1, gameMix + realDt / POST.gameBlendSec) : Math.max(0, gameMix - realDt / POST.gameBlendSec);
    u.uGame.value = gameMix;
    if (gameMix > 0) {
      if (frame.winnerFeet && frame.winnerHead) {
        v3.copy(frame.winnerFeet).project(camera);
        v3b.copy(frame.winnerHead).project(camera);
        const cx = (v3.x + v3b.x) / 4 + 0.5;
        const cy = (v3.y + v3b.y) / 4 + 0.5;
        const hgt = Math.abs(v3b.y - v3.y) / 2;
        u.uGameCenter.value.set(cx, cy);
        u.uGameRadius.value = Math.max(0.06, 2 * hgt);
      } else {
        // No winner on screen: everything goes grey (a radius of 0 would hit undefined smoothstep).
        u.uGameCenter.value.set(-10, -10);
        u.uGameRadius.value = 0.01;
      }
    }

    u.uVignette.value = POST.vignette;
    u.uGrain.value = tier === 'low' || tier === 'potato' ? 0 : POST.grain;

    chromaLeft = Math.max(0, chromaLeft - realDt * 1000);
    u.uChroma.value = chromaLeft > 0 ? chromaAmount * (chromaLeft / chromaTotal) : 0;
    flashLeft = Math.max(0, flashLeft - realDt * 1000);
    u.uFlash.value = flashLeft > 0 ? flashAmount * (flashLeft / flashTotal) : 0;

    u.uVhs.value = vhs ? 1 : 0;
    u.uWipe.value = frame.wipe;
    const letterWant = frame.letterbox ? POST.letterbox : 0;
    const lr = POST.letterboxRate * realDt;
    u.uLetter.value = letterWant > u.uLetter.value ? Math.min(letterWant, u.uLetter.value + lr) : Math.max(letterWant, u.uLetter.value - lr);

    redLeft = Math.max(0, redLeft - realDt);
    const rf = redLeft > 0 ? 1 - redLeft / POST.redSec : 1;
    u.uRed.value = redLeft > 0 ? POST.redPeak * Math.sin(Math.PI * rf) : 0;

    waveLeft = Math.max(0, waveLeft - realDt);
    if (waveLeft > 0 && tier === 'high') {
      const f = 1 - waveLeft / POST.waveSec;
      v3.copy(waveWorld).project(camera);
      u.uWave.value.set((v3.x + 1) / 2, (v3.y + 1) / 2, POST.waveRadius * Math.sqrt(f), 1 - f);
    } else {
      u.uWave.value.set(0, 0, 0, 0);
    }
  }

  /** Effects that must show on every tier (Potato renders them through one FinalPass). */
  function potatoNeedsFinal(): boolean {
    return u.uGame.value > 0 || u.uVhs.value > 0 || u.uWipe.value > 0 || u.uFlash.value > 0 || u.uLetter.value > 0
      || u.uRed.value > 0 || u.uChroma.value > 0;
  }

  function drawOverlay(): void {
    if (!overlayScene || !overlayCamera || !overlayVisible()) return;
    // The pop material ignores depth, so no depth clear is needed.
    const auto = renderer.autoClear;
    renderer.autoClear = false;
    renderer.setRenderTarget(null);
    renderer.render(overlayScene, overlayCamera);
    renderer.autoClear = auto;
  }

  /** True when the overlay has something to draw (an instanced mesh with instances). */
  function overlayVisible(): boolean {
    let any = false;
    overlayScene?.traverseVisible((o) => {
      const g = (o as { geometry?: { instanceCount?: number } }).geometry;
      if (g && (g.instanceCount === undefined || g.instanceCount > 0)) any = true;
    });
    return any;
  }

  const chain: GamePost = {
    uniforms: u,
    get tier() {
      return tier;
    },
    get finalActive() {
      return finalActive;
    },
    render(scene, camera, view) {
      const realDt = lastNow < 0 ? 0 : Math.min(0.1, Math.max(0, (view.now - lastNow) / 1000));
      lastNow = view.now;
      updateUniforms(camera, view, realDt);
      if (composer && renderPass) {
        renderPass.scene = scene;
        renderPass.camera = camera;
        composer.render(realDt);
        finalActive = true;
      } else if (potatoNeedsFinal()) {
        renderer.getDrawingBufferSize(size);
        if (potatoViaTarget) {
          if (!potatoRt) potatoRt = new WebGLRenderTarget(size.x, size.y, { type: HalfFloatType });
          else if (potatoRt.width !== size.x || potatoRt.height !== size.y) potatoRt.setSize(size.x, size.y);
          renderer.setRenderTarget(potatoRt);
          renderer.render(scene, camera);
          renderer.setRenderTarget(null);
          u.tDiffuse.value = potatoRt.texture;
          potatoQuad.render(renderer);
        } else {
          if (!potatoTex || potatoTex.image.width !== size.x || potatoTex.image.height !== size.y) {
            potatoTex?.dispose();
            potatoTex = new FramebufferTexture(size.x, size.y);
            potatoTex.minFilter = LinearFilter;
            potatoTex.magFilter = LinearFilter;
          }
          renderer.setRenderTarget(null);
          renderer.render(scene, camera);
          renderer.copyFramebufferToTexture(potatoTex);
          u.tDiffuse.value = potatoTex;
          potatoCopyQuad.render(renderer);
        }
        u.tDiffuse.value = null;
        finalActive = true;
      } else {
        renderer.setRenderTarget(null);
        renderer.render(scene, camera);
        finalActive = false;
      }
      drawOverlay();
    },
    setTier(t) {
      if (t === tier && (t === 'potato' || composer)) return;
      tier = t;
      build();
    },
    pulseChromatic(amount, ms) {
      if (chromaLeft > 0 && amount < chromaAmount * (chromaLeft / chromaTotal)) return;
      chromaAmount = amount;
      chromaLeft = ms;
      chromaTotal = Math.max(1, ms);
    },
    setVhs(on) {
      vhs = on;
    },
    resize(w, h, ratio) {
      cssW = Math.max(1, w);
      cssH = Math.max(1, h);
      dpr = ratio;
      renderer.setPixelRatio(ratio);
      renderer.setSize(cssW, cssH, false);
      if (composer) {
        composer.setPixelRatio(ratio);
        composer.setSize(cssW, cssH);
      }
    },
    shockwave(x, y, z) {
      if (tier !== 'high') return;
      waveWorld.set(x, z, y);
      waveLeft = POST.waveSec;
    },
    redPulse() {
      redLeft = POST.redSec;
    },
    flash(color, amount, ms) {
      u.uFlashColor.value.copy(color);
      flashAmount = amount;
      flashLeft = ms;
      flashTotal = Math.max(1, ms);
    },
    setFrame(f) {
      Object.assign(frame, f);
    },
    setOverlay(scene, camera) {
      overlayScene = scene;
      overlayCamera = camera;
    },
    tuneGrade(preset, g) {
      Object.assign(presets[preset], g);
    },
    potatoViaTarget(on) {
      potatoViaTarget = on;
    },
    tuneBloom(threshold, strength, radius) {
      bloomTune = { threshold, strength, radius };
      if (bloom) {
        bloom.threshold = threshold;
        bloom.strength = strength;
        bloom.radius = radius;
      }
    },
  };
  return chain;
}
